import { Alert } from "@/src/lib/productAlert";
import { Text } from "@/src/components/ui/BrandText";
import { border, surface, text as uiText } from "@/src/theme/tokens";
import { Ionicons } from "@/src/components/ui/ionicons";
import { router, useLocalSearchParams } from "expo-router";
import {
  collection,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  Platform,
  Pressable,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { PostCard } from "../../src/components/PostCard";
import { AccountSwitcher } from "../../src/components/AccountSwitcher";
import { ProfileActions } from "../../src/components/ProfileActions";
import { IdentityHeader } from "../../src/components/IdentityHeader";
import { contentMaxWidths, readingContent } from "@/src/theme/layout";
import { useBlockedUsers } from "../../src/hooks/useBlockedUsers";
import { useFollowStatus } from "../../src/hooks/useFollowStatus";
import { useMyPostLikes } from "../../src/hooks/useMyPostLikes";
import { usePublicProfile } from "../../src/hooks/usePublicProfile";
import { useActiveAccount } from "../../src/hooks/useActiveAccount";
import { blockUser, unblockUser } from "../../src/lib/blocks";
import { auth, db } from "../../src/lib/firebase";
import { reportPost, reportUser } from "../../src/lib/reports";
import { SUBJECT_LABEL, TRACK_LABEL } from "../../src/lib/taxonomy";
import { togglePostLike } from "../../src/lib/postLikes";
import { followIdentity, unfollowIdentity } from "../../src/lib/follows";
import {
  getAccountType,
  getIdentityBadges,
  getVerificationStatus,
  isLegacyPostForPersonalIdentity,
} from "../../src/lib/identity";
import type { AccountType, IdentityEntitlements, PublicIdentitySnapshot, VerificationStatus } from "../../src/types/identity";

const BG = surface.secondary;
const CARD = surface.primary;
const BORDER = border.subtle;
const MUTED = uiText.muted;

type FirePost = {
  id: string;
  authorUid: string;
  authorIdentityId?: string;
  authorSnapshot?: PublicIdentitySnapshot;
  authorUsername?: string;
  authorEmail?: string;
  authorRole?: "student" | "teacher";
  authorAccountType?: AccountType;
  authorVerificationStatus?: VerificationStatus;
  authorEntitlements?: IdentityEntitlements;
  teacherVerified?: boolean;
  text: string;
  subject?: string;
  track?: string;
  createdAt?: any;
  likesCount?: number;
  likedBy?: string[];
  visibility?: "global" | "group";
  groupId?: string | null;
  groupName?: string;
  groupIsPublic?: boolean;
};

type PublicGroupItem = {
  id: string;
  name: string;
  subject?: string;
  membersCount?: number;
};

function chunkArray<T>(arr: T[], size: number) {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    out.push(arr.slice(i, i + size));
  }
  return out;
}

function uniqById(items: FirePost[]) {
  const map = new Map<string, FirePost>();

  for (const item of items) {
    map.set(item.id, item);
  }

  return Array.from(map.values()).sort((a, b) => {
    const aMs =
      typeof a?.createdAt?.toMillis === "function" ? a.createdAt.toMillis() : 0;
    const bMs =
      typeof b?.createdAt?.toMillis === "function" ? b.createdAt.toMillis() : 0;
    return bMs - aMs;
  });
}

function postsFromAuthorQuery(
  snap: { docs: readonly { id: string; data: () => unknown }[] },
  authorKey: string,
  profileId: string
) {
  return snap.docs
    .map((d) => ({ id: d.id, ...(d.data() as any) }) as FirePost)
    .filter((post) => authorKey !== "legacy" || isLegacyPostForPersonalIdentity(post, profileId));
}

export default function PublicProfileScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const viewer = auth.currentUser;
  const blockedUserIds = useBlockedUsers(viewer?.uid);

  const profileId = useMemo(() => String(id || ""), [id]);
  const { profile, loadingProfile, error: profileError, retry: retryProfile } = usePublicProfile(profileId);
  const { accounts, activeAccountId } = useActiveAccount();
  const profileType = profile?.accountType;
  const profileLoaded = !!profile;
  const isPersonProfile = !!profile && profileType !== "institution";
  const isControlledProfile = useMemo(
    () => !!profileId && accounts.some((account) => account.accountId === profileId),
    [accounts, profileId]
  );
  const isActiveIdentityProfile = !!activeAccountId && activeAccountId === profileId;
  const followersCount = profile?.followersCount ?? 0;
  const followingCount = profile?.followingCount ?? 0;
  const followersLabel = followersCount === 1 ? "abonné" : "abonnés";
  const followingLabel = followingCount === 1 ? "abonnement" : "abonnements";
  const { following, loading: loadingFollow, error: followStatusError, retry: retryFollowStatus } = useFollowStatus(
    activeAccountId,
    profileLoaded && !isActiveIdentityProfile ? profileId : null
  );
  const profileIdentity = useMemo(() => {
    const accountType = getAccountType(profile) ?? "student";
    const badges = profile?.badges?.length ? profile.badges : getIdentityBadges(profile);
    return {
      identityId: profile?.identityId ?? profileId,
      handle: profile?.username ?? "",
      displayName: accountType === "institution"
        ? profile?.institutionName?.trim() || profile?.displayName?.trim() || profile?.username || "École Neqraw"
        : profile?.displayName?.trim() || profile?.username || "Utilisateur",
      avatarUrl: profile?.photoURL?.trim() || null,
      accountType,
      badges: accountType === "institution"
        ? badges.map((badge) => ({
          ...badge,
          label: badge.kind === "institution_premium"
            ? "École premium"
            : getVerificationStatus(profile) === "verified"
              ? "École vérifiée"
              : "École",
        }))
        : badges,
    } as const;
  }, [profile, profileId]);

  const [posts, setPosts] = useState<FirePost[]>([]);
  const { likedPostIds, setLikedPostIds } = useMyPostLikes(
    posts.map((post) => post.id),
    viewer?.uid
  );
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [groups, setGroups] = useState<PublicGroupItem[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(true);

  const [error, setError] = useState<string | null>(null);
  const [postsRetryKey, setPostsRetryKey] = useState(0);
  const [followBusy, setFollowBusy] = useState(false);
  const [followActionError, setFollowActionError] = useState<string | null>(null);
  const followBusyRef = useRef(false);

  const [likesOverlay, setLikesOverlay] = useState<
    Record<string, { likedBy: string[]; likesCount: number }>
  >({});

  useEffect(() => {
    setFollowActionError(null);
  }, [activeAccountId, profileId]);

  useEffect(() => {
    if (!profileId || !profileLoaded || profileType === "institution") {
      setGroups([]);
      setLoadingGroups(false);
      return;
    }

    setLoadingGroups(true);

    const unsub = onSnapshot(
      query(
        collection(db, "groups"),
        where("ownerIdentityId", "==", profileId),
        where("isPublic", "==", true),
        limit(50)
      ),
      (snap) => {
        setGroups(
          snap.docs
            .map((item) => {
              const data = item.data() as any;
              return {
                id: item.id,
                name: String(data?.name ?? "Groupe"),
                subject: data?.subject,
                membersCount: Number(data?.membersCount ?? 0),
              } satisfies PublicGroupItem;
            })
            .sort((a, b) => (b.membersCount ?? 0) - (a.membersCount ?? 0))
            .slice(0, 12)
        );
        setLoadingGroups(false);
      },
      () => {
        setGroups([]);
        setLoadingGroups(false);
      }
    );

    return () => unsub();
  }, [profileId, profileLoaded, profileType]);

  useEffect(() => {
    if (!profileId) return;
    if (!profileLoaded) {
      setLoadingPosts(true);
      return;
    }
    const viewerUid = viewer?.uid;
    if (!viewerUid) {
      setPosts([]);
      setLoadingPosts(false);
      return;
    }

    let active = true;
    const unsubscribers: (() => void)[] = [];

    async function start() {
      setLoadingPosts(true);
      setError(null);

      try {
        const [membershipSnap, publicGroupsSnap] = await Promise.all([
          activeAccountId
            ? getDocs(collection(db, "identityGroupMemberships", activeAccountId, "groups"))
            : Promise.resolve(null),
          getDocs(
            query(
              collection(db, "groups"),
              where("isPublic", "==", true),
              limit(1000)
            )
          ),
        ]);

        if (!active) return;

        const myGroupIds = membershipSnap
          ? membershipSnap.docs
              .filter((d) => !(d.data() as { isPublic?: boolean }).isPublic)
              .map((d) => d.id)
          : [];
        const myGroupChunks = chunkArray(myGroupIds, 10);

        const authorQueries = [
          { field: "authorIdentityId", key: "identity" },
          ...(profileType === "institution"
            ? []
            : [{ field: "authorUid", key: "legacy" }]),
        ] as const;
        const globalPosts = new Map<string, FirePost[]>();
        const publicGroupPosts = new Map<string, FirePost[]>();
        const privateChunkPosts = new Map<string, FirePost[]>();

        const pushMerged = () => {
          const merged = uniqById([
            ...Array.from(globalPosts.values()).flat(),
            ...Array.from(publicGroupPosts.values()).flat(),
            ...Array.from(privateChunkPosts.values()).flat(),
          ]);

          setPosts(merged);
          setLoadingPosts(false);
        };

        const publicGroupChunks = chunkArray(
          publicGroupsSnap.docs.map((groupSnap) => groupSnap.id),
          10
        );

        authorQueries.forEach(({ field, key: authorKey }) => {
          const qGlobal = query(
            collection(db, "posts"),
            where(field, "==", profileId),
            where("visibility", "==", "global"),
            where("moderationStatus", "==", "approved"),
            orderBy("createdAt", "desc"),
            limit(50)
          );
          const unsubGlobal = onSnapshot(
            qGlobal,
            (snap) => {
              globalPosts.set(authorKey, postsFromAuthorQuery(snap, authorKey, profileId));
              pushMerged();
            },
            (err) => {
              if (__DEV__) console.warn("public profile global posts error", err?.code ?? "unknown");
              globalPosts.set(authorKey, []);
              pushMerged();
            }
          );
          unsubscribers.push(unsubGlobal);

          publicGroupChunks.forEach((groupChunk, index) => {
            const key = `${authorKey}_public_${index}`;
            const qPublicGroups = query(
              collection(db, "posts"),
              where(field, "==", profileId),
              where("visibility", "==", "group"),
              where("groupId", "in", groupChunk),
              where("moderationStatus", "==", "approved"),
              orderBy("createdAt", "desc"),
              limit(50)
            );
            const unsub = onSnapshot(
              qPublicGroups,
              (snap) => {
                publicGroupPosts.set(key, postsFromAuthorQuery(snap, authorKey, profileId));
                pushMerged();
              },
              (err) => {
                if (__DEV__) console.warn("public profile public group posts error", err?.code ?? "unknown");
                publicGroupPosts.set(key, []);
                pushMerged();
              }
            );
            unsubscribers.push(unsub);
          });

          myGroupChunks.forEach((groupChunk, index) => {
            const key = `${authorKey}_private_${index}`;
            const qPrivateMemberGroups = query(
              collection(db, "posts"),
              where(field, "==", profileId),
              where("visibility", "==", "group"),
              where("groupId", "in", groupChunk),
              where("moderationStatus", "==", "approved"),
              orderBy("createdAt", "desc"),
              limit(50)
            );
            const unsub = onSnapshot(
              qPrivateMemberGroups,
              (snap) => {
                privateChunkPosts.set(key, postsFromAuthorQuery(snap, authorKey, profileId));
                pushMerged();
              },
              (err) => {
                if (__DEV__) console.warn("public profile private member posts error", err?.code ?? "unknown");
                privateChunkPosts.set(key, []);
                pushMerged();
              }
            );
            unsubscribers.push(unsub);
          });
        });

        if (publicGroupChunks.length === 0 && myGroupChunks.length === 0) pushMerged();
      } catch {
        if (__DEV__) console.warn("public profile load posts error");
        if (!active) return;
        setPosts([]);
        setLoadingPosts(false);
        setError("Impossible de charger les posts visibles.");
      }
    }

    start();

    return () => {
      active = false;
      unsubscribers.forEach((u) => u());
    };
  }, [activeAccountId, postsRetryKey, profileLoaded, profileId, profileType, viewer?.uid]);

  const visiblePosts = useMemo(() => {
    const u = auth.currentUser;
    if (isPersonProfile && blockedUserIds.has(profileId)) return [];

    return posts.map((p) => {
      const o = likesOverlay[p.id];
      const merged = o
        ? { ...p, likedBy: o.likedBy, likesCount: o.likesCount }
        : p;

      const likedByArr: string[] = Array.isArray(merged.likedBy)
        ? merged.likedBy
        : [];
      const liked = !!u && (likedPostIds.has(merged.id) || likedByArr.includes(u.uid));
      const likes = Number.isInteger(merged.likesCount)
        ? merged.likesCount
        : likedByArr.length;

      return {
        ...merged,
        liked,
        likes,
        isMine: !!u && merged.authorUid === u.uid,
        subjectLabel: merged.subject
          ? SUBJECT_LABEL[merged.subject as keyof typeof SUBJECT_LABEL] ??
            merged.subject
          : "Général",
      };
    });
  }, [blockedUserIds, isPersonProfile, likedPostIds, likesOverlay, posts, profileId]);

  // Ownership is proven by the fresh server-resolved account list, never by
  // a client-provided accountId or by comparing it with the Firebase UID.
  const isOwnProfile = isControlledProfile;
  const isBlocked = isPersonProfile && blockedUserIds.has(profileId);

  function handleAccountSelected(nextAccountId: string) {
    if (isOwnProfile && nextAccountId !== profileId) {
      router.replace(`/profile/${nextAccountId}`);
    }
  }

  async function toggleFollow() {
    if (!activeAccountId || !profileId || isActiveIdentityProfile || (isBlocked && !following) || followBusyRef.current || followStatusError) return;

    try {
      followBusyRef.current = true;
      setFollowBusy(true);
      setFollowActionError(null);
      if (following) {
        await unfollowIdentity(activeAccountId, profileId);
      } else {
        await followIdentity(activeAccountId, profileId);
      }
    } catch (e: any) {
      if (__DEV__) console.warn("[profile] follow failed", e?.code ?? "unknown");
      setFollowActionError("L’abonnement n’a pas pu être modifié. Réessaie.");
      Alert.alert("Action impossible", "L'abonnement n'a pas pu être modifié. Réessaie.");
    } finally {
      followBusyRef.current = false;
      setFollowBusy(false);
    }
  }

  async function reportThisProfile() {
    if (!viewer || !profileId || isOwnProfile) return;

    try {
      await reportUser({
        reporterUid: viewer.uid,
        targetUid: profileId,
        reason: "other",
        details: "Signalement depuis le profil",
      });
      Alert.alert("Signalement envoyé", "Merci, ce profil sera examiné.");
    } catch (e: any) {
      if (__DEV__) console.warn("[profile] report failed", e?.code ?? "unknown");
      Alert.alert("Signalement impossible", "Le profil n'a pas pu être signalé. Réessaie.");
    }
  }

  async function toggleBlockProfile() {
    if (!viewer || !profileId || isOwnProfile) return;

    try {
      if (isBlocked) {
        await unblockUser({ uid: viewer.uid, blockedUid: profileId });
        Alert.alert("Utilisateur débloqué", "Ses contenus peuvent réapparaître.");
      } else {
        await blockUser({ uid: viewer.uid, blockedUid: profileId });
        Alert.alert("Utilisateur bloqué", "Ses contenus seront masqués pour toi.");
      }
    } catch (e: any) {
      if (__DEV__) console.warn("[profile] block failed", e?.code ?? "unknown");
      Alert.alert("Action impossible", "Ce profil n'a pas pu être mis à jour. Réessaie.");
    }
  }

  async function reportProfilePost(postId: string) {
    if (!viewer) return;

    try {
      await reportPost({
        reporterUid: viewer.uid,
        postId,
        reason: "other",
        details: "Signalement depuis le profil public",
      });
      Alert.alert("Signalement envoyé", "Merci, ce post sera examiné.");
    } catch (e: any) {
      if (__DEV__) console.warn("[profile] post report failed", e?.code ?? "unknown");
      Alert.alert("Signalement impossible", "La publication n'a pas pu être signalée. Réessaie.");
    }
  }

  async function toggleLike(postId: string) {
    const u = auth.currentUser;
    if (!u) return;

    const current = visiblePosts.find((p) => p.id === postId);
    const baseLikedBy: string[] = Array.isArray(current?.likedBy)
      ? current.likedBy
      : [];
    const baseLikes: number =
      typeof current?.likesCount === "number" ? current.likesCount : baseLikedBy.length;

    const alreadyLiked = likedPostIds.has(postId) || baseLikedBy.includes(u.uid);
    const nextLikedBy = alreadyLiked
      ? baseLikedBy.filter((x) => x !== u.uid)
      : [...baseLikedBy, u.uid];

    const nextLikes = alreadyLiked ? Math.max(0, baseLikes - 1) : baseLikes + 1;

    const prevOverlay = likesOverlay[postId];

    setLikesOverlay((prev) => ({
      ...prev,
      [postId]: { likedBy: nextLikedBy, likesCount: nextLikes },
    }));
    setLikedPostIds((prev) => {
      const next = new Set(prev);
      if (alreadyLiked) next.delete(postId);
      else next.add(postId);
      return next;
    });

    try {
      await togglePostLike({ postId, uid: u.uid, currentlyLiked: alreadyLiked });
    } catch {
      setLikedPostIds((prev) => {
        const next = new Set(prev);
        if (alreadyLiked) next.add(postId);
        else next.delete(postId);
        return next;
      });
      setLikesOverlay((prev) => {
        const copy = { ...prev };
        if (prevOverlay) copy[postId] = prevOverlay;
        else delete copy[postId];
        return copy;
      });
    }
  }

  const renderPost = ({ item }: { item: any }) => {
    return (
      <PostCard
        item={item}
        liked={item.liked}
        likes={item.likes}
        isMine={item.isMine}
        onToggleLike={toggleLike}
        onOpenActions={(post) => {
          if ((post as any).isMine) return;
          const actions: any[] = [
            { text: "Annuler", style: "cancel" },
            { text: "Signaler", onPress: () => reportProfilePost(post.id) },
          ];
          if (isPersonProfile) actions.push({
              text: isBlocked ? "Debloquer le profil" : "Bloquer le profil",
              style: isBlocked ? "default" : "destructive",
              onPress: toggleBlockProfile,
          });
          Alert.alert("Post", "Choisis une action.", actions);
        }}
        disableAuthorLink
        onSharePost={(post) =>
          router.push({
            pathname: "/share/post/[postId]",
            params: { postId: post.id },
          })
        }
      />
    );
  };

  if (!profileId) {
    return (
      <SafeAreaView
        style={{ flex: 1, backgroundColor: BG, alignItems: "center", justifyContent: "center" }}
      >
        <Text style={{ color: uiText.primary }}>Profil introuvable.</Text>
      </SafeAreaView>
    );
  }

  if (loadingProfile) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: BG }}>
        <View style={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
          <ActivityIndicator color={uiText.brand} />
          <Text style={{ color: MUTED, marginTop: 10 }}>Chargement du profil...</Text>
        </View>
      </SafeAreaView>
    );
  }

  const profileErrorMessage =
    profileError === "not-found"
      ? "Profil introuvable."
      : profileError === "load-failed"
        ? "Impossible de charger le profil."
        : null;
  const visibleError = error ?? profileErrorMessage;

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: BG }} edges={["top", "bottom", "left", "right"]}>
      <FlatList
        style={{ flex: 1, width: "100%", minHeight: 0 }}
        data={visiblePosts}
        keyExtractor={(item) => item.id}
        renderItem={renderPost}
        contentContainerStyle={{ ...readingContent(contentMaxWidths.profile), paddingBottom: 24, paddingHorizontal: 16 }}
        ItemSeparatorComponent={() => <View style={{ height: 10 }} />}
        initialNumToRender={6}
        maxToRenderPerBatch={6}
        windowSize={7}
        removeClippedSubviews={Platform.OS === "android"}
        ListHeaderComponent={
          <View style={{ paddingTop: 8, paddingBottom: 12, gap: 10 }}>
            <View style={{ flexDirection: "row", alignItems: "center" }}>
              <Pressable
                onPress={() => router.back()}
                accessibilityRole="button"
                accessibilityLabel="Retour"
                hitSlop={8}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  backgroundColor: CARD,
                  borderWidth: 1,
                  borderColor: BORDER,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Ionicons name="chevron-back" size={20} color={uiText.primary} />
              </Pressable>
            </View>

            {accounts.length > 1 ? (
              <AccountSwitcher
                compact
                label="Changer d’identité"
                onAccountSelected={handleAccountSelected}
              />
            ) : null}

            <View
              style={{
                backgroundColor: CARD,
                borderRadius: 18,
                borderWidth: 1,
                borderColor: BORDER,
                padding: 14,
              }}
            >
              <IdentityHeader identity={profileIdentity} />

              {profile?.mainSubject || profile?.track ? (
                <Text style={{ color: MUTED, marginTop: 10 }}>
                  {profile.mainSubject
                    ? SUBJECT_LABEL[profile.mainSubject as keyof typeof SUBJECT_LABEL] ?? profile.mainSubject
                    : ""}
                  {profile.mainSubject && profile.track ? " · " : ""}
                  {profile.track
                    ? TRACK_LABEL[profile.track as keyof typeof TRACK_LABEL] ?? profile.track
                    : ""}
                </Text>
              ) : null}

              <View style={{ flexDirection: "row", gap: 18, marginTop: 12 }}>
                <Pressable
                  onPress={() => router.push(`/profile/${profileId}/connections?type=followers`)}
                  accessibilityRole="button"
                  accessibilityLabel={`${followersCount} ${followersLabel}, afficher la liste`}
                  style={({ pressed }) => ({ opacity: pressed ? 0.65 : 1 })}
                >
                  <Text style={{ color: uiText.primary, fontWeight: "800" }}>
                    {followersCount} {followersLabel}
                  </Text>
                </Pressable>
                <Pressable
                  onPress={() => router.push(`/profile/${profileId}/connections?type=following`)}
                  accessibilityRole="button"
                  accessibilityLabel={`${followingCount} ${followingLabel}, afficher la liste`}
                  style={({ pressed }) => ({ opacity: pressed ? 0.65 : 1 })}
                >
                  <Text style={{ color: uiText.primary, fontWeight: "800" }}>
                    {followingCount} {followingLabel}
                  </Text>
                </Pressable>
              </View>

              {!!activeAccountId && !isActiveIdentityProfile ? (
                isControlledProfile ? (
                  <View style={{ flexDirection: "row", marginTop: 12 }}>
                    <Pressable
                      onPress={() => void toggleFollow()}
                      accessibilityRole="button"
                      accessibilityLabel={following ? "Ne plus suivre cette identité" : "Suivre cette identité"}
                      disabled={followBusy || loadingFollow || !!followStatusError || (isBlocked && !following)}
                      style={{
                        minHeight: 40,
                        justifyContent: "center",
                        borderRadius: 12,
                        borderWidth: 1,
                        borderColor: following ? surface.primary : surface.selected,
                        backgroundColor: following ? surface.primary : surface.selected,
                        paddingHorizontal: 12,
                        opacity: followBusy || loadingFollow || !!followStatusError || (isBlocked && !following) ? 0.65 : 1,
                      }}
                    >
                      <Text style={{ color: uiText.primary, fontWeight: "900" }}>
                        {followBusy ? "…" : loadingFollow ? "Chargement…" : following ? "Abonné" : "Suivre"}
                      </Text>
                    </Pressable>
                  </View>
                ) : (
                <ProfileActions identity={profileIdentity} blocked={isBlocked} onReport={isPersonProfile ? reportThisProfile : undefined} onToggleBlock={isPersonProfile ? toggleBlockProfile : undefined}>
                  <Pressable
                    onPress={() => void toggleFollow()}
                    accessibilityRole="button"
                    accessibilityLabel={following ? "Ne plus suivre cette identité" : "Suivre cette identité"}
                    disabled={followBusy || loadingFollow || !!followStatusError || (isBlocked && !following)}
                    style={{
                      minHeight: 40,
                      justifyContent: "center",
                      borderRadius: 12,
                      borderWidth: 1,
                      borderColor: following ? surface.primary : surface.selected,
                      backgroundColor: following ? surface.primary : surface.selected,
                      paddingHorizontal: 12,
                      opacity: followBusy || loadingFollow || !!followStatusError || (isBlocked && !following) ? 0.65 : 1,
                    }}
                  >
                    <Text style={{ color: uiText.primary, fontWeight: "900" }}>
                      {followBusy ? "…" : loadingFollow ? "Chargement…" : following ? "Abonné" : "Suivre"}
                    </Text>
                  </Pressable>
                </ProfileActions>
                )
              ) : null}
              {followStatusError ? (
                <View style={{ flexDirection: "row", alignItems: "center", gap: 8, marginTop: 8 }}>
                  <Text style={{ color: uiText.danger, fontSize: 12 }}>État du suivi indisponible.</Text>
                  <Pressable onPress={retryFollowStatus} accessibilityRole="button" style={{ paddingVertical: 4 }}>
                    <Text style={{ color: uiText.brand, fontSize: 12, fontWeight: "800" }}>Réessayer</Text>
                  </Pressable>
                </View>
              ) : null}
              {followActionError ? <Text style={{ color: uiText.danger, marginTop: 8 }}>{followActionError}</Text> : null}

              <Text style={{ color: MUTED, marginTop: 10 }}>
                {visiblePosts.length} {visiblePosts.length > 1 ? "publications visibles" : "publication visible"}
              </Text>
              {isBlocked ? (
                <Text style={{ color: uiText.danger, marginTop: 8 }}>
                  Profil bloque : les posts sont masques.
                </Text>
              ) : null}
            </View>

            {profileType !== "institution" ? <View
              style={{
                backgroundColor: CARD,
                borderRadius: 18,
                borderWidth: 1,
                borderColor: BORDER,
                padding: 16,
                gap: 10,
              }}
            >
              <Text style={{ color: uiText.primary, fontWeight: "900" }}>Groupes publics</Text>

              {loadingGroups ? (
                <Text style={{ color: MUTED }}>Chargement...</Text>
              ) : groups.length === 0 ? (
                <Text style={{ color: MUTED }}>Aucun groupe public visible.</Text>
              ) : (
                groups.map((group) => (
                  <Pressable
                    key={group.id}
                    onPress={() => router.push(`/group/${group.id}`)}
                    style={{
                      borderRadius: 14,
                      borderWidth: 1,
                      borderColor: BORDER,
                      backgroundColor: surface.primary,
                      padding: 12,
                    }}
                  >
                    <Text style={{ color: uiText.primary, fontWeight: "900" }}>{group.name}</Text>
                    <Text style={{ color: MUTED, marginTop: 4 }}>
                      {(group.membersCount ?? 0).toLocaleString()} membres
                      {group.subject ? ` - ${SUBJECT_LABEL[group.subject as keyof typeof SUBJECT_LABEL] ?? group.subject}` : ""}
                    </Text>
                  </Pressable>
                ))
              )}
            </View> : null}

            {visibleError ? (
              <View
                style={{
                  backgroundColor: surface.danger,
                  borderWidth: 1,
                  borderColor: border.danger,
                  borderRadius: 16,
                  padding: 12,
                }}
              >
                <Text style={{ color: uiText.danger, fontWeight: "800" }}>Erreur</Text>
                <Text style={{ color: uiText.danger, marginTop: 6 }}>{visibleError}</Text>
                {profileError !== "not-found" ? (
                  <Pressable
                    onPress={() => {
                      retryProfile();
                      setPostsRetryKey((value) => value + 1);
                    }}
                    accessibilityRole="button"
                    accessibilityLabel="Réessayer de charger le profil"
                    style={{ alignSelf: "flex-start", marginTop: 8, paddingVertical: 6 }}
                  >
                    <Text style={{ color: uiText.brand, fontWeight: "900" }}>Réessayer</Text>
                  </Pressable>
                ) : null}
              </View>
            ) : null}

            <Text style={{ color: uiText.primary, fontWeight: "900", fontSize: 16 }}>
              Publications
            </Text>
          </View>
        }
        ListEmptyComponent={
          loadingPosts ? (
            <View style={{ paddingTop: 20, alignItems: "center" }}>
              <ActivityIndicator color={uiText.brand} />
              <Text style={{ color: MUTED, marginTop: 10 }}>Chargement des posts...</Text>
            </View>
          ) : (
            <View
              style={{
                backgroundColor: CARD,
                borderRadius: 18,
                borderWidth: 1,
                borderColor: BORDER,
                padding: 16,
              }}
            >
              <Text style={{ color: uiText.primary, fontWeight: "900" }}>Aucune publication visible</Text>
              <Text style={{ color: MUTED, marginTop: 6 }}>
                Rien à afficher pour le moment.
              </Text>
            </View>
          )
        }
        showsVerticalScrollIndicator={Platform.OS === "web"}
      />
    </SafeAreaView>
  );
}
